// Single-field login page. Submit-on-enter, auto-focus, full-width button on
// mobile. Errors render inline with a polite message — never the raw stack.
//
// Auth is held by the parent hook (useAuth) so the same instance can flip the
// global state and let the router redirect us to '/'.
//
// Sprint access-session-recovery (D2-D6) — this page now renders ONE of two
// distinct screens, never both: the password form (server says "no
// application session") or the Access re-entry screen (the identity gate in
// front of the app rejected the request before it reached the server). The
// two layers are real and different (D6); this sprint only stops confusing
// them.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiHttpError } from '../lib/api';
import { PRODUCT_NAME } from '../lib/branding';
import { useTranslation } from '../i18n';

export interface LoginPageProps {
  authed: boolean;
  // D2/D3/D4/D6 — true when /api/auth/me was rejected by the Cloudflare
  // Access identity gate (D1), not by the server saying "no session".
  // Renders the re-entry screen INSTEAD of the password form (early
  // return below) — the two never render together.
  gateExpired: boolean;
  // Challenger R1 change 12 — /api/auth/me failed as NETWORK_ERROR at
  // boot (the classifier's only other failure branch). The password form
  // stays up (D6 is not reopened), but a secondary, non-automatic
  // affordance offers the same D3 navigation, for the engine where an
  // Access rejection never surfaces as an `opaqueredirect`.
  meNetworkError: boolean;
  login: (password: string) => Promise<void>;
}

// D3 — the query param is what breaks Workbox's precache match (the
// default `ignoreURLParametersMatching` only strips `utm_*`/`fbclid`), so
// this exact shape forces the browser past the cached shell to the
// network, where Cloudflare Access can complete the login and mint the
// cookie. Pure + exported so tests can assert the shape without stubbing
// `Date.now()` globally.
export function reauthUrl(now: number = Date.now()): string {
  return `/?reauth=${now}`;
}

// D3/D4 — a top-level navigation. Never `fetch`, never `<iframe>`, never
// `window.open`: only a top-level navigation can follow the edge's
// redirect to the Access login and come back with the cookie set in the
// SAME cookie jar the app runs in.
export function goToReauth(): void {
  window.location.assign(reauthUrl());
}

export function LoginPage({ authed, gateExpired, meNetworkError, login }: LoginPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (authed) navigate('/', { replace: true });
  }, [authed, navigate]);

  useEffect(() => {
    if (!gateExpired) inputRef.current?.focus();
  }, [gateExpired]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!password) {
      setError(t('login.passwordEmpty'));
      return;
    }
    setSubmitting(true);
    try {
      await login(password);
      navigate('/', { replace: true });
    } catch (err) {
      const msg =
        err instanceof ApiHttpError ? err.message : t('login.couldNotSignIn');
      setError(msg);
      setPassword('');
      inputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  if (gateExpired) {
    // Challenger R1 change 5 — EC-3 self-report: `?reauth=` still on the
    // URL AND we're still `gate-expired` after boot means the round trip
    // through the D3 navigation did not leave a usable session behind
    // (the iOS-standalone cookie-jar hypothesis, tracked as its own
    // edge-case row). Distinct copy — never a silent repeat of the same
    // message on what is now a KNOWN-bad retry.
    const stillBlocked = searchParams.has('reauth');
    return (
      <div
        className="flex min-h-full items-center justify-center bg-background px-4 py-safe-top pb-safe-bottom"
        data-testid="login-page"
      >
        <div className="w-full max-w-sm bg-background border border-border rounded-lg p-6 shadow-xl">
          <h1 className="text-display font-semibold text-foreground mb-6">{PRODUCT_NAME}</h1>
          <p
            className="text-body font-medium text-foreground mb-1"
            data-testid="access-gate-title"
          >
            {t('accessGate.title')}
          </p>
          <p
            className="text-body text-muted-foreground mb-5"
            data-testid="access-gate-message"
          >
            {stillBlocked ? t('accessGate.stillBlockedMessage') : t('accessGate.message')}
          </p>
          <button
            type="button"
            onClick={goToReauth}
            className="w-full bg-action hover:bg-action/90 text-action-foreground font-medium rounded-lg px-4 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-action"
            data-testid="access-gate-reenter"
          >
            {t('accessGate.reenterButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-full items-center justify-center bg-background px-4 py-safe-top pb-safe-bottom"
      data-testid="login-page"
    >
      <div className="w-full max-w-sm bg-background border border-border rounded-lg p-6 shadow-xl">
        {/* Sprint 5.7 (D28) — single-user product; the marketing subtitle
            ("Sign in to continue." / "Manage your AI coding agents.") was
            removed as pure noise. Only the product wordmark + the sign-in
            form remain. */}
        <h1 className="text-display font-semibold text-foreground mb-6">{PRODUCT_NAME}</h1>

        <form onSubmit={onSubmit} noValidate>
          <label
            htmlFor="password"
            className="block text-body font-medium text-foreground mb-2"
          >
            {t('login.passwordLabel')}
          </label>
          <input
            id="password"
            ref={inputRef}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            className="w-full bg-background text-foreground placeholder:text-muted-foreground/70 rounded-lg border border-border px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
            data-testid="password-input"
          />

          {error ? (
            <p
              role="alert"
              className="mt-3 text-body text-status-error"
              data-testid="login-error"
            >
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="mt-5 w-full bg-action hover:bg-action/90 disabled:bg-popover text-action-foreground font-medium rounded-lg px-4 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-action"
            data-testid="login-submit"
          >
            {submitting ? t('login.signingIn') : t('login.signIn')}
          </button>
        </form>

        {meNetworkError ? (
          <div className="mt-5 pt-5 border-t border-border">
            <p className="text-body-chat text-muted-foreground mb-2">
              {t('accessGate.networkErrorHint')}
            </p>
            <button
              type="button"
              onClick={goToReauth}
              className="w-full bg-background hover:bg-accent text-foreground font-medium rounded-lg border border-border px-4 py-3 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
              data-testid="access-gate-network-reenter"
            >
              {t('accessGate.networkErrorButton')}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
